import { useState, useEffect } from "react";
import axiosInstance from "@/config/axios";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { toast } from "sonner";
import { Trash2, Eye, Download, Calendar, Database } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function Leads() {
  const [leadLists, setLeadLists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedList, setSelectedList] = useState(null);

  useEffect(() => {
    fetchLeadLists();
  }, []);

  const fetchLeadLists = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axiosInstance.get(`${API}/api/admin/leads`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLeadLists(response.data);
    } catch (error) {
      console.error("Error fetching lead lists:", error);
      toast.error("Failed to fetch lead lists");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (listId) => {
    if (!window.confirm("Are you sure you want to delete this lead list?")) return;
    
    try {
      const token = localStorage.getItem("token");
      await axiosInstance.delete(`${API}/api/admin/leads/${listId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Lead list deleted");
      fetchLeadLists();
    } catch (error) {
      console.error("Error deleting lead list:", error);
      toast.error("Failed to delete lead list");
    }
  };

  const handleDownload = async (listId, format) => {
    try {
      const token = localStorage.getItem("token");
      const response = await axiosInstance.get(`${API}/api/admin/leads/${listId}/download?format=${format}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const list = leadLists.find(l => l.id === listId);
      const blob = new Blob([format === 'json' ? JSON.stringify(response.data, null, 2) : response.data], {
        type: format === 'json' ? 'application/json' : 'text/csv'
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${list.name}.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(`Downloaded as ${format.toUpperCase()}`);
    } catch (error) {
      console.error("Error downloading:", error);
      toast.error("Failed to download");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-slate-500">Loading lead lists...</div>
      </div>
    );
  }

  if (leadLists.length === 0) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="text-center py-12">
          <Database className="w-16 h-16 mx-auto text-slate-300 mb-4" />
          <h3 className="text-xl font-semibold text-slate-700 mb-2">No Lead Lists Yet</h3>
          <p className="text-slate-500 mb-4">
            Save results from Apify scraper runs to create your lead lists here.
          </p>
          <p className="text-sm text-slate-400">
            Go to Data → Apify Actors → Run a scraper → View Results → Save to Leads
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full p-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-slate-800">Lead Lists</h1>
        <p className="text-slate-600 mt-2">Manage your saved lead lists from Apify scraper results</p>
      </div>

      <div className="space-y-4">
        {leadLists.map(list => (
          <Card key={list.id} className="hover:shadow-lg transition-shadow">
            <CardHeader>
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <CardTitle className="text-xl">{list.name}</CardTitle>
                  <CardDescription className="mt-2 space-y-1">
                    <div className="flex items-center gap-2 text-sm">
                      <Calendar className="w-4 h-4" />
                      <span>Scraped: {new Date(list.scraped_at).toLocaleString()}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <Database className="w-4 h-4" />
                      <span>{list.total_rows} leads</span>
                      {list.visible_columns && (
                        <Badge variant="outline" className="ml-2">
                          {list.visible_columns.length} columns visible
                        </Badge>
                      )}
                    </div>
                    {list.actor_name && (
                      <div className="text-xs text-slate-500">
                        Source: {list.actor_name}
                      </div>
                    )}
                  </CardDescription>
                </div>
                
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setSelectedList(list.id === selectedList ? null : list.id)}
                  >
                    <Eye className="w-4 h-4 mr-2" />
                    {list.id === selectedList ? 'Hide' : 'View'}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleDownload(list.id, 'json')}
                  >
                    <Download className="w-4 h-4 mr-2" />
                    JSON
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleDownload(list.id, 'csv')}
                  >
                    <Download className="w-4 h-4 mr-2" />
                    CSV
                  </Button>
                  <Button
                    size="sm"
                    variant="destructive"
                    onClick={() => handleDelete(list.id)}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            
            {selectedList === list.id && (
              <CardContent className="pt-0">
                <LeadListTable list={list} />
              </CardContent>
            )}
          </Card>
        ))}
      </div>
    </div>
  );
}

function LeadListTable({ list }) {
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  
  // Reuse the same table rendering logic
  const items = list.data;
  const visibleColumns = list.visible_columns || [];
  const columnOrder = list.column_order || [];
  const defaultColumnWidth = list.default_column_width || 200;
  
  if (!items || items.length === 0) {
    return <div className="text-center py-8 text-slate-500">No data</div>;
  }

  // Get columns in the saved order
  let keys = visibleColumns.length > 0 ? visibleColumns : Object.keys(items[0]);
  if (columnOrder.length > 0) {
    keys = keys.sort((a, b) => {
      const indexA = columnOrder.indexOf(a);
      const indexB = columnOrder.indexOf(b);
      if (indexA === -1) return 1;
      if (indexB === -1) return -1;
      return indexA - indexB;
    });
  }

  // Pagination
  const totalPages = Math.ceil(items.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = Math.min(startIndex + rowsPerPage, items.length);
  const paginatedItems = items.slice(startIndex, endIndex);
  
  // Reset to page 1 if current page becomes invalid
  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(1);
    }
  }, [currentPage, totalPages]);

  // Helper function to calculate average from review distribution
  const calculateAverageRating = (reviewDist) => {
    if (!reviewDist || typeof reviewDist !== 'object') return null;
    
    const ratings = {
      oneStar: reviewDist.oneStar || 0,
      twoStar: reviewDist.twoStar || 0,
      threeStar: reviewDist.threeStar || 0,
      fourStar: reviewDist.fourStar || 0,
      fiveStar: reviewDist.fiveStar || 0
    };
    
    const totalReviews = Object.values(ratings).reduce((a, b) => a + b, 0);
    if (totalReviews === 0) return null;
    
    const weightedSum = ratings.oneStar * 1 + ratings.twoStar * 2 + ratings.threeStar * 3 + ratings.fourStar * 4 + ratings.fiveStar * 5;
    return (weightedSum / totalReviews).toFixed(1);
  };

  // Helper function to format tooltip content (presentable, not JSON)
  const formatTooltipContent = (value, columnName) => {
    if (value === null || value === undefined || value === '') return '-';
    
    // Handle objects
    if (typeof value === 'object') {
      // Opening hours
      if (columnName && columnName.toLowerCase().includes('opening') && columnName.toLowerCase().includes('hour')) {
        const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
        return days.map(day => {
          const hours = value[day];
          if (!hours) return null;
          
          // Handle different data structures
          let hoursText = '';
          if (Array.isArray(hours)) {
            // If it's an array, join the elements
            hoursText = hours.join(', ');
          } else if (typeof hours === 'object') {
            // If it's an object with open/close or other structure
            if (hours.open && hours.close) {
              hoursText = `${hours.open} - ${hours.close}`;
            } else {
              hoursText = JSON.stringify(hours);
            }
          } else {
            // Simple string
            hoursText = String(hours);
          }
          
          return `${day.charAt(0).toUpperCase() + day.slice(1)}: ${hoursText}`;
        }).filter(Boolean).join('\n');
      }
      
      // People also search (array of objects with query)
      if (Array.isArray(value)) {
        if (value.length === 0) return 'No items';
        
        // Array of objects
        if (typeof value[0] === 'object') {
          return value.map((item, idx) => {
            const name = item.query || item.question || item.name || item.title || item.text;
            if (name) return `${idx + 1}. ${name}`;
            return `${idx + 1}. ${Object.values(item)[0]}`;
          }).join('\n');
        }
        
        // Array of primitives
        return value.join(', ');
      }
      
      // Generic objects - format as key: value pairs
      return Object.entries(value).map(([key, val]) => {
        if (typeof val === 'object') return `${key}: [Object]`;
        return `${key}: ${val}`;
      }).join('\n');
    }
    
    return String(value);
  };

  const renderCellValue = (value, columnName) => {
    // Handle null/undefined/empty
    if (value === null || value === undefined || value === '') {
      return <span title="-">-</span>;
    }
    
    // Handle booleans
    if (typeof value === 'boolean') {
      return <span title={value ? 'Yes' : 'No'}>{value ? 'Yes' : 'No'}</span>;
    }
    
    // Handle numbers
    if (typeof value === 'number') {
      const formatted = value.toLocaleString();
      return <span title={formatted}>{formatted}</span>;
    }
    
    // Get tooltip content
    const tooltipText = formatTooltipContent(value, columnName);
    
    // Handle objects and arrays
    if (typeof value === 'object') {
      // Special handling for review distribution
      if (columnName && columnName.toLowerCase().includes('review') && columnName.toLowerCase().includes('distribution')) {
        const avg = calculateAverageRating(value);
        if (avg) return <span title={tooltipText}>⭐ {avg} avg</span>;
        return <span title="-">-</span>;
      }
      
      // IMPORTANT: Check for opening hours BEFORE generic array handling
      const colNameLower = columnName ? columnName.toLowerCase().replace(/[_\s]/g, '') : '';
      const isOpeningHours = colNameLower.includes('opening') || colNameLower.includes('hours') || colNameLower === 'openinghours';
      
      if (isOpeningHours && Array.isArray(value)) {
        // Array format: [{day: "Monday", hours: "9AM-5PM"}, ...]
        const formattedLines = value.map(item => {
          if (typeof item === 'string') return item;
          if (item.day && item.hours) return `${item.day}: ${item.hours}`;
          if (item.day && item.open && item.close) return `${item.day}: ${item.open} - ${item.close}`;
          // Handle other structures - just stringify
          if (typeof item === 'object') {
            const entries = Object.entries(item).map(([k, v]) => `${k}=${v}`).join(' ');
            return entries;
          }
          return String(item);
        });
        
        // Use separator that works reliably in tooltips
        const tooltipText = formattedLines.join(' • ');
        const displayText = formattedLines[0] || 'Opening hours';
        
        return (
          <span title={tooltipText} className="cursor-help">
            {displayText.substring(0, 35)}{formattedLines.length > 1 ? `... (${formattedLines.length} days)` : ''}
          </span>
        );
      }
      
      if (isOpeningHours && !Array.isArray(value)) {
        // Object format with day keys: {monday: "9AM-5PM", tuesday: "9AM-5PM", ...}
        const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
        const formattedLines = [];
        
        days.forEach(day => {
          const hours = value[day];
          if (!hours) return;
          
          let hoursText = '';
          
          // Check data type and format accordingly
          if (Array.isArray(hours)) {
            // Array format: ["9:00 AM – 5:00 PM"]
            hoursText = hours.join(', ');
          } else if (typeof hours === 'object' && hours !== null) {
            // Object format: {open: "9AM", close: "5PM"}
            if (hours.open && hours.close) {
              hoursText = `${hours.open} - ${hours.close}`;
            } else {
              // Other object structures
              hoursText = JSON.stringify(hours);
            }
          } else {
            // String format
            hoursText = String(hours);
          }
          
          if (hoursText) {
            formattedLines.push(`${day.charAt(0).toUpperCase() + day.slice(1)}: ${hoursText}`);
          }
        });
        
        if (formattedLines.length === 0) {
          return <span title="No opening hours data">-</span>;
        }
        
        // Use separator instead of newlines for better tooltip compatibility
        const tooltipText = formattedLines.join(' • ');
        const displayText = formattedLines[0] || 'Opening hours';
        
        return (
          <span 
            title={tooltipText}
            className="cursor-help"
          >
            {displayText.substring(0, 35)}{formattedLines.length > 1 ? '...' : ''}
          </span>
        );
      }
      
      // Handle arrays (generic handling after opening hours check)
      if (Array.isArray(value)) {
        if (value.length === 0) return <span title="No items">-</span>;
        
        // Check if array of primitives
        const firstItem = value[0];
        if (typeof firstItem !== 'object') {
          const display = value.length <= 3 ? value.join(', ') : `${value.slice(0, 2).join(', ')}... (+${value.length - 2} more)`;
          return <span title={tooltipText} className="cursor-help">{display}</span>;
        }
        
        // Array of objects - show count and first item preview
        const preview = firstItem.name || firstItem.title || firstItem.query || firstItem.question || Object.values(firstItem)[0];
        const display = preview ? String(preview).substring(0, 30) : 'Item 1';
        return (
          <span title={tooltipText} className="cursor-help">
            {display}... ({value.length} items)
          </span>
        );
      }
      
      // Handle complex objects (Q&A, etc.)
      const objectKeys = Object.keys(value);
      if (objectKeys.length === 0) return <span title="Empty object">-</span>;
      
      // Create a readable summary for other objects
      const summary = objectKeys.slice(0, 2).map(key => {
        const val = value[key];
        if (typeof val === 'object') return `${key}`;
        return `${key}: ${String(val).substring(0, 15)}`;
      }).join(', ');
      
      return (
        <span title={tooltipText} className="cursor-help">
          {summary}{objectKeys.length > 2 ? '...' : ''}
        </span>
      );
    }
    
    // Check if value is a URL and make it clickable
    if (typeof value === 'string' && (value.startsWith('http://') || value.startsWith('https://'))) {
      return (
        <a 
          href={value} 
          target="_blank" 
          rel="noopener noreferrer" 
          className="text-blue-600 hover:underline"
          title={value}
        >
          {value.length > 40 ? value.substring(0, 40) + '...' : value}
        </a>
      );
    }
    
    // Handle strings - ALWAYS show tooltip
    const strValue = String(value);
    if (strValue.length > 50) {
      return (
        <span title={strValue} className="cursor-help">
          {strValue.substring(0, 50)}...
        </span>
      );
    }
    
    // Even short strings get tooltip
    return <span title={strValue} className="cursor-help">{strValue}</span>;
  };

  return (
    <div className="space-y-4">
      <style>{`
        .custom-scrollbar-leads::-webkit-scrollbar {
          width: 14px;
          height: 14px;
        }
        .custom-scrollbar-leads::-webkit-scrollbar-track {
          background: #e2e8f0;
          border-radius: 0;
        }
        .custom-scrollbar-leads::-webkit-scrollbar-thumb {
          background: #64748b;
          border-radius: 4px;
          border: 2px solid #e2e8f0;
        }
        .custom-scrollbar-leads::-webkit-scrollbar-thumb:hover {
          background: #475569;
        }
        .custom-scrollbar-leads::-webkit-scrollbar-corner {
          background: #e2e8f0;
        }
        .custom-scrollbar-leads {
          scrollbar-width: thin;
          scrollbar-color: #64748b #e2e8f0;
        }
      `}</style>
      
      <div 
        className="custom-scrollbar-leads relative border border-slate-200 rounded-lg bg-white"
        style={{ 
          height: '500px',
          overflowY: 'scroll',
          overflowX: 'scroll',
        }}
      >
        <table className="min-w-full divide-y divide-slate-200" style={{ width: 'max-content' }}>
          <thead className="bg-slate-100 sticky top-0 z-10 shadow-sm">
            <tr>
              <th 
                className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase border-b border-slate-200 bg-slate-100 sticky left-0 z-20"
                style={{ minWidth: '60px', width: '60px' }}
              >
                #
              </th>
              {keys.map(key => (
                <th 
                  key={key} 
                  className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase border-b border-slate-200 bg-slate-100"
                  style={{ 
                    minWidth: `${defaultColumnWidth}px`,
                    width: `${defaultColumnWidth}px`,
                    maxWidth: `${defaultColumnWidth}px`
                  }}
                >
                  <span className="truncate" title={key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}>
                    {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-slate-200">
            {paginatedItems.map((item, idx) => (
              <tr key={startIndex + idx} className="hover:bg-slate-50 transition-colors">
                <td 
                  className="px-4 py-3 text-sm text-slate-600 border-r border-slate-200 font-medium sticky left-0 bg-white z-10"
                  style={{ minWidth: '60px', width: '60px' }}
                >
                  {startIndex + idx + 1}
                </td>
                {keys.map(key => (
                  <td 
                    key={key} 
                    className="px-4 py-3 text-sm text-slate-800 border-r border-slate-200"
                    style={{ 
                      minWidth: `${defaultColumnWidth}px`,
                      width: `${defaultColumnWidth}px`,
                      maxWidth: `${defaultColumnWidth}px`,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {renderCellValue(item[key], key)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      
      {/* Pagination Controls */}
      <div className="flex items-center justify-between bg-slate-50 p-3 rounded-lg border border-slate-200">
        <div className="flex items-center gap-2">
          <span className="text-sm text-slate-600">
            Showing {startIndex + 1} to {endIndex} of {items.length} leads
          </span>
        </div>
        
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => setCurrentPage(1)}
            disabled={currentPage === 1}
          >
            First
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
            disabled={currentPage === 1}
          >
            Previous
          </Button>
          <span className="text-sm text-slate-600 px-2">
            Page {currentPage} of {totalPages}
          </span>
          <Button
            size="sm"
            variant="outline"
            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
          >
            Next
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => setCurrentPage(totalPages)}
            disabled={currentPage === totalPages}
          >
            Last
          </Button>
          
          <select
            value={rowsPerPage}
            onChange={(e) => {
              setRowsPerPage(Number(e.target.value));
              setCurrentPage(1);
            }}
            className="h-8 text-xs border border-slate-300 rounded px-2 ml-2"
          >
            <option value="25">25 rows</option>
            <option value="50">50 rows</option>
            <option value="100">100 rows</option>
            <option value="200">200 rows</option>
          </select>
        </div>
      </div>
    </div>
  );
}
